// Standalone exhibition page at /exhibition/:slug (and /exhibition/:slug/:venueSlug).
//
// URL rules:
//   /exhibition/mumbai              → EventDetailsModal, multi-venue selector, no venue selected
//   /exhibition/mumbai/andheri      → EventDetailsModal, Andheri pill pre-selected + details shown
//   /exhibition/mumbai/worli        → EventDetailsModal, Worli pill pre-selected + details shown
//   /exhibition/mumbai/andheri/thank-you  → Universal Thank-You page
//
// For single-venue exhibitions (venues=[]), URL always stays /exhibition/:slug
// and behaviour matches previous single-venue mode.
//
// On CTA click (Register / Notify), an inline modal replaces the details modal;
// URL still remains /exhibition/:slug/:venueSlug (no /register segment).
// On submit → navigate to /exhibition/:slug[/:venueSlug]/thank-you.
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import RegistrationModal from "@/components/sections/RegistrationModal";
import NotifyModal from "@/components/sections/NotifyModal";
import EventDetailsModal from "@/components/sections/EventDetailsModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExhibitionPage() {
  // /exhibition/:slug OR /exhibition/:slug/:venueSlug
  const params = useParams();
  const slug = params.slug;
  const venueSlugParam = params.venueSlug;
  const navigate = useNavigate();
  const loc = useLocation();

  const [exhibition, setExhibition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  // Internal view state — does NOT touch the URL for view switching.
  // Values: "details" (default) | "form" (Register/Notify modal open)
  const [view, setView] = useState("details");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setView("details");
    axios.get(`${API}/exhibitions/by-slug/${slug}`)
      .then((r) => { if (alive) setExhibition(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.status === 404 ? "Exhibition not found" : "Could not load exhibition"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (exhibition?.name) document.title = `${exhibition.name} · Rudralife Exhibition`;
  }, [exhibition?.name]);

  // Normalised venue list (admins may or may not have configured venues[]).
  const venues = useMemo(() => {
    const raw = Array.isArray(exhibition?.venues) ? exhibition.venues : [];
    return raw.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [exhibition]);

  const multiVenue = venues.length > 0;

  // Resolve selected venue from URL segment.
  const selectedVenue = useMemo(() => {
    if (!multiVenue) return null;
    if (!venueSlugParam) return null;
    return venues.find((v) => v.slug === venueSlugParam) || null;
  }, [venues, venueSlugParam, multiVenue]);

  // ---- routing helpers ----
  const baseUrl = `/exhibition/${slug}`;
  const venueUrl = (v) => v ? `${baseUrl}/${v.slug}` : baseUrl;
  const thankYouUrl = () =>
    multiVenue && selectedVenue
      ? `${baseUrl}/${selectedVenue.slug}/thank-you`
      : `${baseUrl}/thank-you`;

  // If URL has a venueSlug that doesn't exist on the exhibition, redirect to base.
  useEffect(() => {
    if (loading || !exhibition) return;
    if (!multiVenue) return;
    if (venueSlugParam && !venues.find((v) => v.slug === venueSlugParam)) {
      navigate(baseUrl, { replace: true });
    }
  }, [loading, exhibition, multiVenue, venueSlugParam, venues, baseUrl, navigate]);

  // If a single-venue exhibition is opened with a stale venueSlug, redirect to base.
  useEffect(() => {
    if (loading || !exhibition) return;
    if (!multiVenue && venueSlugParam) {
      navigate(baseUrl, { replace: true });
    }
  }, [loading, exhibition, multiVenue, venueSlugParam, baseUrl, navigate]);

  if (loading) {
    return <div className="rl-standalone-loading" data-testid="exh-loading">Loading exhibition…</div>;
  }
  if (err) {
    return (
      <div className="rl-standalone-error" data-testid="exh-error">
        <h2>{err}</h2>
        <p>The link you opened may have changed.</p>
        <a className="rl-btn rl-btn-primary" href="/">← Back to Rudralife</a>
      </div>
    );
  }
  if (!exhibition) return null;

  const closeToHome = () => navigate("/");

  // ---- resolve the effective "city-like" object for the form modals ----
  // In multi-venue mode with a selection → use venue-specific dates/timings/venue/address + inherit country/dial from exhibition.
  // In single-venue mode → use exhibition top-level fields (legacy behaviour).
  const activeCity = (() => {
    const base = {
      ...exhibition,
      dates: "",
      dateRange: null,
    };
    const source = multiVenue && selectedVenue
      ? {
          start_date: selectedVenue.start_date,
          end_date: selectedVenue.end_date,
          timings: selectedVenue.timings,
          venue: selectedVenue.venue_name,
          address: selectedVenue.address,
          status: selectedVenue.status,
        }
      : {
          start_date: exhibition.start_date,
          end_date: exhibition.end_date,
          timings: exhibition.timings,
          venue: exhibition.venue,
          address: exhibition.address,
          status: exhibition.status,
        };
    const merged = { ...base, ...source };
    if (merged.start_date && merged.end_date) {
      merged.dateRange = { start: merged.start_date, end: merged.end_date };
      // simple readable formatting for the form header sub-line
      const opts = { day: "numeric", month: "long", year: "numeric" };
      merged.dates = `${new Date(merged.start_date).toLocaleDateString("en-IN", opts)} – ${new Date(merged.end_date).toLocaleDateString("en-IN", opts)}`;
    } else {
      merged.dates = "Schedule TBA";
    }
    return merged;
  })();

  // ---- handlers ----
  const handleSelectVenue = (v) => {
    if (!v) return;
    navigate(venueUrl(v)); // updates the URL to /exhibition/:slug/:venueSlug
  };

  const handleRegister = () => {
    if (multiVenue && !selectedVenue) return;
    setView("form");
  };

  const backToDetails = () => setView("details");

  // ---- render ----
  // When view === "form", pick RegistrationModal (live) or NotifyModal (soon).
  if (view === "form") {
    const status = activeCity.status;
    if (status === "live") {
      return (
        <div className="rl-standalone-bg" data-testid="exh-standalone-register">
          <RegistrationModal
            exhibition={activeCity}
            slug={slug}
            venueSlug={selectedVenue ? selectedVenue.slug : ""}
            urlSync
            onClose={backToDetails}
          />
        </div>
      );
    }
    return (
      <div className="rl-standalone-bg" data-testid="exh-standalone-notify">
        <NotifyModal
          city={activeCity}
          type={activeCity.type || "domestic"}
          slug={slug}
          venueSlug={selectedVenue ? selectedVenue.slug : ""}
          urlSync
          onClose={backToDetails}
        />
      </div>
    );
  }

  return (
    <div className="rl-standalone-bg" data-testid="exh-standalone-default">
      <EventDetailsModal
        city={exhibition}
        venues={multiVenue ? venues : []}
        selectedVenue={selectedVenue}
        onSelectVenue={handleSelectVenue}
        onClose={closeToHome}
        onRegister={handleRegister}
      />
    </div>
  );
  // thankYouUrl is exposed only for RegistrationModal/NotifyModal via the URL
  // they read from slug/venueSlug props — nothing else to wire here.
}
